/**
 * localStorage shim for per-viewer, per-friend overlay color overrides.
 *
 * Storage key: `qs:overlay-color:<viewerId>:<ownerId>` → hex string.
 *
 * Scope: a single override per viewer-friend pair, not per-poll. So a
 * viewer changing Alice → fuchsia in poll A also makes Alice fuchsia
 * in poll B and on subsequent reloads of poll A.
 *
 * NEVER persisted server-side — the requirement is explicitly that
 * this UX preference does NOT add a Firestore field. Lost on browser
 * data clear, intentionally.
 *
 * All helpers no-op gracefully if `window.localStorage` is missing
 * (SSR, locked-down browsers, vitest jsdom in unusual configs).
 */

const KEY_PREFIX = "qs:overlay-color:";

function safeStorage() {
  try {
    if (typeof window === "undefined") return null;
    if (!window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function buildKey(viewerId, ownerId) {
  if (!viewerId || !ownerId) return null;
  return `${KEY_PREFIX}${viewerId}:${ownerId}`;
}

const HEX_RE = /^#[0-9a-f]{6}$/i;

/**
 * Read the override for a single viewer→owner pair.
 * Returns the hex string or `null` if no valid override is set.
 */
export function getColorOverride(viewerId, ownerId) {
  const storage = safeStorage();
  if (!storage) return null;
  const key = buildKey(viewerId, ownerId);
  if (!key) return null;
  try {
    const v = storage.getItem(key);
    if (!v) return null;
    if (!HEX_RE.test(v)) return null;
    return v;
  } catch {
    return null;
  }
}

/**
 * Write a hex override for a viewer→owner pair. Hex must match
 * `#rrggbb` (case-insensitive). Invalid input no-ops.
 */
export function setColorOverride(viewerId, ownerId, hex) {
  const storage = safeStorage();
  if (!storage) return;
  const key = buildKey(viewerId, ownerId);
  if (!key) return;
  if (typeof hex !== "string" || !HEX_RE.test(hex)) return;
  try {
    storage.setItem(key, hex);
  } catch {
    /* full disk, private mode quota etc — silent */
  }
}

/**
 * Clear the override for a viewer→owner pair (so the auto-assigned
 * color takes over again).
 */
export function clearColorOverride(viewerId, ownerId) {
  const storage = safeStorage();
  if (!storage) return;
  const key = buildKey(viewerId, ownerId);
  if (!key) return;
  try {
    storage.removeItem(key);
  } catch {
    /* silent */
  }
}

/**
 * Bulk-load the override map for a viewer across a known list of
 * owner uids. Returns `{ [ownerId]: hex }` for entries that have an
 * override; missing keys are omitted.
 *
 * Used by callers building the `overridesByOwnerId` map fed into
 * `resolveOwnerColor`.
 */
export function loadColorOverrides(viewerId, ownerIds = []) {
  if (!Array.isArray(ownerIds) || !ownerIds.length) return {};
  const out = {};
  for (const ownerId of ownerIds) {
    const v = getColorOverride(viewerId, ownerId);
    if (v) out[ownerId] = v;
  }
  return out;
}
